import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { taskService } from '../../api/tasks';
import type { TaskComment } from '../../types/entities/task.entity';
import { ActorChip } from './ActorChip';
import { formatAppDateTime } from '../../utils/dateFormats';

type Props = {
    taskId: string;
    onChanged?: () => void;
};

/** Comment thread of a task. Authors are the user or a bot. */
export const TaskComments = ({ taskId, onChanged }: Props) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const queryKey = ['task-comments', taskId];
    const [draft, setDraft] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingBody, setEditingBody] = useState('');

    const { data: comments = [], isLoading } = useQuery<TaskComment[]>({
        queryKey,
        queryFn: () => taskService.getComments(taskId),
        refetchOnWindowFocus: true,
    });

    const refresh = async () => {
        await queryClient.invalidateQueries({ queryKey });
        onChanged?.();
    };

    const addMutation = useMutation({
        mutationFn: (body: string) => taskService.addComment(taskId, body),
        onSuccess: async () => {
            setDraft('');
            await refresh();
        },
        onError: () => enqueueSnackbar(t('comments.saveError'), { variant: 'error' }),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, body }: { id: string; body: string }) => taskService.updateComment(taskId, id, body),
        onSuccess: async () => {
            setEditingId(null);
            await refresh();
        },
        onError: () => enqueueSnackbar(t('comments.saveError'), { variant: 'error' }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => taskService.deleteComment(taskId, id),
        onSuccess: refresh,
        onError: () => enqueueSnackbar(t('comments.deleteError'), { variant: 'error' }),
    });

    const submit = () => {
        const body = draft.trim();
        if (body) {
            addMutation.mutate(body);
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('comments.title')} {comments.length > 0 ? `(${comments.length})` : ''}
            </Typography>
            {isLoading ? (
                <CircularProgress size={18} />
            ) : (
                <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 320, overflow: 'auto' }}>
                    {comments.length === 0 && (
                        <Typography variant="body2" color="text.secondary">{t('comments.empty')}</Typography>
                    )}
                    {comments.map((comment) => (
                        <Paper key={comment.id} variant="outlined" sx={{ p: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <ActorChip actor={comment.author} />
                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                    {formatAppDateTime(new Date(comment.created_at))}
                                    {comment.updated_at !== comment.created_at ? ` · ${t('comments.edited')}` : ''}
                                </Typography>
                                {comment.is_mine && editingId !== comment.id && (
                                    <Tooltip title={t('comments.edit')}>
                                        <IconButton size="small" onClick={() => { setEditingId(comment.id); setEditingBody(comment.body); }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title={t('comments.delete')}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            if (window.confirm(t('comments.deleteConfirm'))) {
                                                deleteMutation.mutate(comment.id);
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            {editingId === comment.id ? (
                                <Stack spacing={1}>
                                    <TextField
                                        value={editingBody}
                                        onChange={(event) => setEditingBody(event.target.value)}
                                        multiline
                                        minRows={2}
                                        fullWidth
                                        size="small"
                                        autoFocus
                                    />
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button size="small" onClick={() => setEditingId(null)}>{t('common.cancel')}</Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={!editingBody.trim() || updateMutation.isPending}
                                            onClick={() => updateMutation.mutate({ id: comment.id, body: editingBody.trim() })}
                                        >
                                            {t('comments.save')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {comment.body}
                                </Typography>
                            )}
                        </Paper>
                    ))}
                </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                            event.preventDefault();
                            submit();
                        }
                    }}
                    placeholder={t('comments.placeholder')}
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                />
                <Button variant="contained" onClick={submit} disabled={!draft.trim() || addMutation.isPending}>
                    {t('comments.add')}
                </Button>
            </Stack>
        </Box>
    );
};
